import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tokensApi } from '../api/index.js';
import { ApiError } from '../api/client.js';
import type { CreatedTokenDto } from '../types.js';

export function TokensPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['tokens'], queryFn: tokensApi.list });
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [newToken, setNewToken] = useState<CreatedTokenDto | null>(null);
  const [copied, setCopied] = useState(false);

  const createMut = useMutation({
    mutationFn: () => tokensApi.create({ name }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['tokens'] });
      setNewToken(data.token);
      setName('');
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to create token'),
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => tokensApi.revoke(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tokens'] }),
  });

  const tokens = data?.tokens ?? [];

  const copyToken = () => {
    void navigator.clipboard.writeText(newToken!.plaintext);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>API Tokens</h1>

      {/* One-time display of newly created token */}
      {newToken && (
        <div className="alert alert-success" style={{ marginBottom: '1.5rem' }}>
          <strong>Token created — copy it now, it won't be shown again:</strong>
          <div style={{ display: 'flex', gap: '.5rem', marginTop: '.5rem', alignItems: 'center' }}>
            <code style={{ background: '#fff', padding: '.25rem .5rem', borderRadius: '4px', fontSize: '.85rem', wordBreak: 'break-all', flex: 1 }}>
              {newToken.plaintext}
            </code>
            <button className="btn btn-outline" onClick={copyToken} style={{ flexShrink: 0 }}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <button onClick={() => setNewToken(null)} style={{ marginTop: '.5rem', background: 'none', border: 'none', color: '#166534', cursor: 'pointer', fontSize: '.875rem', textDecoration: 'underline' }}>
            Dismiss
          </button>
        </div>
      )}

      {/* MCP config snippet */}
      <div className="claude-hint" style={{ marginBottom: '1.5rem' }}>
        <h4>Configure MCP in Claude Desktop</h4>
        <p style={{ fontSize: '.8rem', color: '#1e40af' }}>Add to your claude_desktop_config.json:</p>
        <pre>{`{
  "mcpServers": {
    "autodocument": {
      "url": "${window.location.origin}/mcp",
      "headers": {
        "Authorization": "Bearer <YOUR_TOKEN>"
      }
    }
  }
}`}</pre>
      </div>

      {/* Create form */}
      {error && <div className="alert alert-error">{error}</div>}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontWeight: 600, marginBottom: '1rem' }}>New Token</h2>
        <form onSubmit={(e) => { e.preventDefault(); createMut.mutate(); }} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <label>Token name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Claude Desktop" />
          </div>
          <button className="btn btn-primary" type="submit" disabled={createMut.isPending}>Create</button>
        </form>
      </div>

      {/* Existing tokens */}
      <div className="card">
        {tokens.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No tokens yet.</p>
        ) : (
          <table>
            <thead>
              <tr><th>Name</th><th>Prefix</th><th>Last Used</th><th>Expires</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td><code>{t.prefix}…</code></td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: '.85rem' }}>
                    {t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleDateString() : 'Never'}
                  </td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: '.85rem' }}>
                    {t.expiresAt ? new Date(t.expiresAt).toLocaleDateString() : 'Never'}
                  </td>
                  <td>
                    <span className={`badge ${t.revokedAt ? 'badge-red' : 'badge-green'}`}>
                      {t.revokedAt ? 'revoked' : 'active'}
                    </span>
                  </td>
                  <td>
                    {!t.revokedAt && (
                      <button
                        className="btn btn-danger"
                        style={{ fontSize: '.75rem', padding: '.25rem .5rem' }}
                        onClick={() => { if (confirm('Revoke this token?')) revokeMut.mutate(t.id); }}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
